//MetricsTable.tsx
import {
  Button,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
} from "@mui/material";

import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InfoIcon from "@mui/icons-material/Info";
import React, { useContext } from "react";
import { mapDB, SelectedSchool } from "../../store/mapStore";
import { ScenarioMap, useMapContextStore } from "../../context/MapContext";
import { MetricSelection } from "./sidebar/MetricSelection";
import {
  filterInvalidCategories,
  Metric,
  metricToUnitMap,
} from "../../types/data/dataTypes";
import { SchoolSearchBar } from "./SchoolSearchBar";
import { MetricsByScenario } from "../../types/data/api";
import { formatMetricStrings } from "../../utils/metrics_utils";
import { ChangeTypeComponent } from "./ChangeTypeComponent";
import { PageContext } from "../../context/PageContext";
import { NEW_TEMP_CONTENT } from "../../constants/content";
import { ScenarioExplanationsModal } from "./sidebar/ScenarioExplanationsModal";
import { ScenarioSelector } from "./map/config/ScenarioSelector";

const scenarioKeyMap = {
  "0": 0,
  "4": 4,
};

const scenarioKeyMapSchools = {
  "0": 0,
  "4": 4,
};

const getSortedMetrics = (scenarios: Partial<MetricsByScenario>) => {
  return Object.entries(scenarios)
    .sort()
    .map((entry) => entry[1]);
};

interface MetricsTable {
  data: Partial<MetricsByScenario>;
  school?: SelectedSchool;
  system?: boolean;
  mobile?: boolean;
  edit?: boolean;
}

export const MetricsTable: React.FC<MetricsTable> = ({
  data,
  school,
  system = false,
  mobile = false,
  edit = false,
}) => {
  const theme = useTheme();
  const content = useContext(PageContext);
  const { scenario, metricCategory } = useMapContextStore((store) => store);
  const scenarioIDFixed = school
    ? scenarioKeyMapSchools[scenario]
    : scenarioKeyMap[scenario];
  const metricCategoryKeys = Object.keys(Object.values(data)[0]);
  const metricObjects = Object.values(data)[0]?.[metricCategory]?.metrics;
  const metrics = Object.values(metricObjects ?? {});
  const dataKeys = Object.keys(data).sort();

  return (
    <Box
      sx={{
        overflow: "auto",
        height: { xs: "auto", md: "70svh", lg: "70svh", xl: "75svh" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "baseline",
          mb: 0.5,
          gap: 1,
        }}
      >
        {scenario !== 0 ? <ScenarioExplanationsModal /> : null}
        <Typography
          sx={{ pt: 2, fontSize: { xs: "1rem", md: "1.25rem" } }}
          variant="h3"
        >
          {content[ScenarioMap[scenario]]}
        </Typography>
      </Box>
      {mobile ? <ScenarioSelector dropdown={mobile} /> : null}
      {!mobile ? <SchoolSearchBar /> : null}
      <>
        <Box
          flexDirection={"row"}
          display={"flex"}
          sx={{
            width: "100%",
            justifyContent: "space-between",
            pt: 0.5,
            alignItems: "baseline",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "#012C3B",
              py: 0,
              fontSize: !system ? "1rem" : "1.25rem",
            }}
          >
            {!system ? school.school_data.sch_name : "Systemwide metrics"}
          </Typography>
          {!system ? (
            <Button
              variant="outlined"
              onClick={() => {
                mapDB.selectedSchool
                  .where("active")
                  .equals(1)
                  .modify({ active: 0 });
              }}
            >
              {NEW_TEMP_CONTENT.clear}
            </Button>
          ) : null}
        </Box>
        {!system ? (
          <Typography
            sx={{
              py: 0,
              fontSize: ".875rem",
              marginBottom: 1,
              color: "#012C3B",
              fontStyle: "italic",
            }}
          >
            Serves {school.school_data.sch_level} grades
          </Typography>
        ) : null}
      </>
      {!system ? (
        <ChangeTypeComponent
          changeTypes={
            data?.[0]?.change_type?.metrics?.is_cardozo?.value === true
              ? [
                  {
                    name: "Is Cardozo",
                    value: true,
                    key: "is_cardozo_ms",
                  },
                ]
              : data?.[scenarioIDFixed]?.change_type?.metrics
          }
        />
      ) : null}
      <MetricSelection options={filterInvalidCategories(metricCategoryKeys)} />
      {metricCategory ? (
        <TableContainer sx={{ mt: 1 }}>
          <Table size="small" aria-label="simple table" stickyHeader>
            <colgroup>
              <col width="5%" />
            </colgroup>
            <TableHead sx={{ backgroundColor: theme.palette.primary.light }}>
              <TableCell />

              {dataKeys.map((scenarioNumber) => (
                <TableCell
                  key={scenarioNumber}
                  sx={
                    parseInt(scenarioNumber) === scenarioIDFixed
                      ? {
                          backgroundColor: theme.palette.secondary.main,
                          backgroundOpacity: 0.5,
                          color: "white",
                          textAlign: "center",
                          fontSize: { xs: ".75rem", md: "1rem" },
                        }
                      : {
                          textAlign: "center",
                          fontSize: { xs: ".75rem", md: "1rem" },
                        }
                  }
                >
                  {scenarioNumber === "edit"
                    ? "Your edits"
                    : content[ScenarioMap[scenarioNumber]]}
                </TableCell>
              ))}
            </TableHead>
            {metrics?.length
              ? metrics.map((metric: Metric, index1: number) => {
                  const metricKey = metric.key;
                  return (
                    <>
                      <TableRow
                        sx={{
                          backgroundColor:
                            index1 % 2 === 1 ? "#00000008" : undefined,
                        }}
                      >
                        <TableCell
                          colSpan={5}
                          sx={{
                            [`&.${tableCellClasses.root}`]: {
                              borderBottom: "none",
                              pt: 2,
                              textAlign: "center",
                            },
                          }}
                        >
                          <Tooltip
                            enterTouchDelay={0}
                            title={content[`${metricKey}_Description`]}
                            disableInteractive
                          >
                            <Box
                              color="secondary"
                              sx={{
                                width: "fit-content",
                                flexDirection: "row",
                                justifyContent: "center",
                                display: "flex",
                                gap: 0.5,
                              }}
                            >
                              <InfoIcon
                                color="secondary"
                                sx={{
                                  "&:hover": { opacity: "100%" },
                                  opacity: "60%",
                                }}
                              />
                              <Typography
                                style={{
                                  textAlign: "left",
                                  fontStyle: "italic",
                                  fontSize: ".875rem",
                                }}
                              >
                                {content[`${metric.key}_Title`]}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow
                        key={metric.name}
                        sx={{
                          backgroundColor:
                            index1 % 2 === 1 ? "#00000008" : undefined,
                        }}
                      >
                        <TableCell />
                        {getSortedMetrics(data).map(
                          (scenarioMetrics, index) => {
                            return (
                              <TableCell
                                key={index}
                                sx={{
                                  textAlign: "center",
                                }}
                              >
                                {formatMetricStrings(
                                  scenarioMetrics?.[metricCategory]?.metrics[
                                    metricKey
                                  ]?.value,
                                  metricToUnitMap[metricKey],
                                  metricKey,
                                  index == 1 && edit,
                                )}
                              </TableCell>
                            );
                          },
                        )}
                      </TableRow>
                    </>
                  );
                })
              : null}
          </Table>
        </TableContainer>
      ) : null}
    </Box>
  );
};
